import { Injectable, RendererFactory2, Renderer2 } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { CUSTOMAPIService } from 'app/custom-api.service';

export type AppTheme = 'dark-theme' | 'light-theme';

/** localStorage key for the fluid-typography feature flag (OFF by default). */
const FLUID_TYPE_STORAGE_KEY = 'fluidType';

/**
 * localStorage key for the last theme actually shown. Only the index.html splash reads it: that
 * splash paints before Angular can resolve the user's preference, and it now stays up for the whole
 * cold load, so without this a light-theme user would sit behind a dark screen until their data
 * lands. The app itself still takes its theme from the user record, never from here.
 */
const THEME_STORAGE_KEY = 'appTheme';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private themeSubject = new BehaviorSubject<AppTheme>('dark-theme');
  readonly currentTheme$ = this.themeSubject.asObservable();

  /**
   * Route fragments (nested under /first-party/:entityId/...) where light mode is enabled.
   * Extend this list as each first-party module is themed for light mode
   * (see docs/light-theme-rollout-plan.md). Top-level modules (e.g. /third-party) are
   * gated separately below since they aren't nested under /first-party/.
   */
  private static readonly LIGHT_ENABLED_ROUTES = [
    '/break-down',
    '/collection',
    '/clients',
    '/board',
    '/controls',
    '/pillars',
    '/reporting',
    '/user-settings',
    '/settings',
    '/upperdeck',
    '/multi-entity',
    '/policies',
    '/risk-register',
    '/risk-map',
    '/regulatory-watch',
    '/integrations',
    '/findings',
    '/binary-edge',
    '/remediation',
    '/training-center',
    // Client onboarding (welcome-message → client-form wizard → welcome-video).
    // The hero artwork on these screens stays dark in both themes; only the shell,
    // the wizard modal and its chrome follow the theme.
    '/onboarding',
  ];

  /** Whether the current route supports light mode (drives toggle visibility) */
  private lightThemeAvailable = new BehaviorSubject<boolean>(false);
  readonly isLightThemeAvailable$ = this.lightThemeAvailable.asObservable();

  /** Whether the theme toggle should be disabled (ai-tasks, ai-review, ai-wizard) */
  private themeToggleDisabled = new BehaviorSubject<boolean>(false);
  readonly isThemeToggleDisabled$ = this.themeToggleDisabled.asObservable();

  /** Persisted user preference */
  private userId: string | null = null;
  private userPreference = false; // true = light theme

  constructor(
    private rendererFactory: RendererFactory2,
    private router: Router,
    private customApi: CUSTOMAPIService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.applyTheme(this.themeSubject.value);
    this.applyFluidType(this.fluidTypeEnabled);

    // Watch route changes — auto-apply/revert theme based on route and user preference
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      const url = event.urlAfterRedirects || event.url || '';
      const isLightEnabled =
        url.includes('/third-party') || ThemeService.LIGHT_ENABLED_ROUTES.some(route => url.includes(route));
      this.lightThemeAvailable.next(isLightEnabled);
      // ai-tasks/ai-review/ai-wizard now support both themes (see ai-auto-answering light-theme
      // rollout) — the toggle is only disabled while the ai-wizard modal itself is open
      // (AiWizardComponent calls setThemeToggleDisabled directly), not for the whole route.
      this.themeToggleDisabled.next(false);

      if (isLightEnabled && this.userPreference && this.isDarkMode) {
        this.setTheme('light-theme');
      } else if (!isLightEnabled && this.isLightMode) {
        this.setTheme('dark-theme');
      }
    });
  }

  /** Initialize theme preference from the logged-in user object. Call once after login. */
  initializeFromUser(user: { id: string; lightTheme?: boolean | null }): void {
    if (!user || this.userId === user.id) {
      return;
    }
    this.userId = user.id;
    this.userPreference = !!user.lightTheme;
    // If currently on a light-enabled route and user prefers light, apply immediately
    if (this.lightThemeAvailable.value && this.userPreference) {
      this.setTheme('light-theme');
    }
  }

  /** Persist current theme choice to the backend */
  async saveThemePreference(): Promise<void> {
    if (!this.userId) {
      return;
    }
    this.userPreference = this.isLightMode;
    try {
      await this.customApi.UpdateUser({
        id: this.userId,
        lightTheme: this.isLightMode,
      });
    } catch (e) {
      console.error('Failed to save theme preference', e);
    }
  }

  get currentTheme(): AppTheme {
    return this.themeSubject.value;
  }

  get isDarkMode(): boolean {
    return this.themeSubject.value === 'dark-theme';
  }

  get isLightMode(): boolean {
    return this.themeSubject.value === 'light-theme';
  }

  toggleTheme(): void {
    const newTheme: AppTheme = this.isDarkMode ? 'light-theme' : 'dark-theme';
    this.setTheme(newTheme);
  }

  /**
   * Fluid-typography feature flag (OFF by default). When enabled, the
   * --font-* tokens in styles.scss switch from fixed canonical sizes to
   * fluid clamp() values via the body.fluid-type class.
   */
  get fluidTypeEnabled(): boolean {
    try {
      return localStorage.getItem(FLUID_TYPE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  }

  setFluidType(enabled: boolean): void {
    try {
      localStorage.setItem(FLUID_TYPE_STORAGE_KEY, String(enabled));
    } catch {
      // storage unavailable — apply for this session only
    }
    this.applyFluidType(enabled);
  }

  private applyFluidType(enabled: boolean): void {
    if (enabled) {
      this.renderer.addClass(document.body, 'fluid-type');
    } else {
      this.renderer.removeClass(document.body, 'fluid-type');
    }
  }

  /** Manually override the disabled state — used by modals like the AI Wizard */
  setThemeToggleDisabled(disabled: boolean): void {
    this.themeToggleDisabled.next(disabled);
  }

  setTheme(theme: AppTheme): void {
    this.applyTheme(theme);
    this.themeSubject.next(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // storage unavailable - the splash falls back to its dark default next load
    }
  }

  getCssVar(varName: string): string {
    return getComputedStyle(document.body).getPropertyValue(varName).trim();
  }

  private applyTheme(theme: AppTheme): void {
    const body = document.body;
    this.renderer.removeClass(body, 'dark-theme');
    this.renderer.removeClass(body, 'light-theme');
    this.renderer.addClass(body, theme);
  }
}
